import { ReactElement } from "react";
import { AdminOnly } from "../../../../components/authFilter/AdminOnly";
import { BadgeIndigo, BadgeBlack } from "../../../../components/badge/Badge";
import { OpeningOrderFeeLabel } from "../../../../components/openingOrderFee/OpeningOrderFeeLabel";
import { usePaymentTermsIndicatorDirect } from "../../../../components/paymentTypeIndicator/usePaymentTermsIndicatorDirect";
import { TooltipContainer } from "@sellar-io/components";
import { OrderInitiatorIndicator } from "../../OrderInitiatorIndicator";
import { OrderCheckoutSourceIndicator } from "../../OrderCheckoutSourceIndicator";
import OrderStatusIndicator from "../../orderStatusIndicator/OrderStatusIndicator";
import PaymentStatusIndicator from "../../paymentStatusIndicator/PaymentStatusIndicator";
import { formatOrderListDate } from "./orderListElementHelpers";
import { OrderListElementLayout } from "./OrderListElementLayout";
import { OrderListElementProps, OrderListOrder } from "./orderListElementTypes";
import { SellarDistributionSupplierCutOffAlertContainer } from "../../Alerts/SellarDistributionSupplierCutOffAlert";

export const SupplierOrderListElement = ({
  order,
  selectedOrderId,
  showFulfillment = true,
  showPayment = true,
  showStatus = true,
}: OrderListElementProps): ReactElement => {
  const showSellarCreditFeatures = order.paymentType === "sellarCredit";
  const { full: sellarCreditIcon } = usePaymentTermsIndicatorDirect({
    indicatorType: "sellarCredit",
    tooltipContent: "Sellar Credit",
    overrideText: "",
  });

  const badges = (
    <>
      <div className="flex w-full flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          {showStatus && <OrderStatusIndicator iconOnly order={order} />}
          {showSellarCreditFeatures && sellarCreditIcon}
          {showPayment && !showSellarCreditFeatures && (
            <PaymentStatusIndicator
              paymentStatus={order.paymentStatus}
              voidedAt={order.voidedAt}
              paidAt={order.paidAt}
              paymentType={order.paymentType}
              retailerPaidAt={order.retailerPaidAt}
              paymentDue={order.paymentDue}
              cancelledAt={order.cancelledAt}
              invoiceIssuedAt={order.invoiceIssuedAt}
              invoicesEnabled={order.Supplier.invoicesEnabled}
              iconOnly
            />
          )}
          {!!order.latestAvailableFrom && (
            <TooltipContainer enable text="Pre-order">
              <BadgeIndigo icon="PROGRESS_CLOCK" />
            </TooltipContainer>
          )}
          {order.paymentType === "cardPaymentWithDeposit" && (
            <TooltipContainer enable text="Card payment with deposit">
              <BadgeBlack icon="DEPOSIT_PAYMENT" />
            </TooltipContainer>
          )}
          <AdminOnly>
            <>
              <OrderInitiatorIndicator initiator={order.initiator} />
              <OrderCheckoutSourceIndicator
                checkoutSource={order.checkoutSource}
              />
            </>
          </AdminOnly>
        </div>
        <SellarDistributionSupplierCutOffAlertContainer
          order={{
            ...order,
            supplierFinalisationCutOffAt:
              order.OrderSellarDistributionMeta?.supplierFinalisationCutOffAt ??
              null,
          }}
          mode="badge"
        />
      </div>
    </>
  );

  return (
    <OrderListElementLayout
      order={order}
      selected={selectedOrderId === order.id}
      businessName={order.Retailer.Business.name}
      logoUrl={order.Retailer.Business.logoUrl}
      badges={badges}
      date={<SupplierOrderDate order={order} />}
      businessNameAccessory={
        order.OpeningOrderFee && (
          <OpeningOrderFeeLabel fee={order.OpeningOrderFee} />
        )
      }
      icon={null}
      showFulfillment={showFulfillment}
    />
  );
};

const SupplierOrderDate = ({ order }: { order: OrderListOrder }) => {
  if (order.fulfillmentType === "collection") {
    return (
      <span className="pl-4 text-right text-xs">
        Collection on: {formatOrderListDate(order.deliveryPlannedAt)}
      </span>
    );
  }

  if (!order.dispatchPlannedAt) return null;

  return (
    <span className="pl-4 text-right text-xs">
      Dispatch on: {formatOrderListDate(order.dispatchPlannedAt)}
    </span>
  );
};
